import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { isAccessExpired, getAccountType } from '../context/accountUtils';
import { showToast } from '../context/ToastHandle';

const HEADER_HEIGHT = 56;
const SENTENCE_BUILDER_HEIGHT = 78;
const minTileSize = 125;
const minTileGap = 10;
const maxRGap = 10;
const QUEUE_LIMIT = 5;

const API_PROGRESS_PORTION = 60;
const IMG_PROGRESS_PORTION = 40;

const DEFAULT_GRID = { rows: 5, cols: 8 };
const BOARD_HISTORY_KEY = 'iboardBoardViewHistory';
const BOARD_EXPECTED_DESTINATION_KEY = 'iboardBoardViewExpectedDestination';

const BoardView = ({ showSentenceBuilder = false, sentenceTiles = [], setSentenceTiles = () => {} }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isUnlimited = getAccountType() === 'unlimited';
  const [tiles, setTiles] = useState([]);
  const [loading, setLoading] = useState(true);

  const [gridConfig, setGridConfig] = useState({
    rows: DEFAULT_GRID.rows,
    cols: DEFAULT_GRID.cols,
    scrollable: false,
    square_buttons: true,
  });

  const [gridSettings, setGridSettings] = useState({
    columns: DEFAULT_GRID.cols,
    rows: DEFAULT_GRID.rows,
    TILE_WIDTH: minTileSize,
    TILE_HEIGHT: minTileSize,
    cTILE_GAP: minTileGap,
    rTILE_GAP: minTileGap,
    allowScrollY: false,
  });

  const [coreSettings, setCoreSettings] = useState({
    enabled: false,
    board_id: null,
    side: 'left',
    selected_board: null,
    core_boards: [],
  });
  const [coreTiles, setCoreTiles] = useState([]);
  const [coreGridConfig, setCoreGridConfig] = useState({
    rows: 6,
    cols: 4,
    scrollable: false,
    square_buttons: true,
  });
  const [coreGridSettings, setCoreGridSettings] = useState({
    columns: 4,
    rows: 6,
    TILE_WIDTH: minTileSize,
    TILE_HEIGHT: minTileSize,
    cTILE_GAP: minTileGap,
    rTILE_GAP: minTileGap,
    allowScrollY: false,
  });

  const stickyCoreActive =
    isUnlimited &&
    Boolean(coreSettings?.enabled && coreSettings?.board_id) &&
    String(coreSettings.board_id) !== String(id);

  const [loaderProgress, setLoaderProgress] = useState(0);
  const [showLoader, setShowLoader] = useState(false);

  const [speakingQueue, setSpeakingQueue] = useState([]);
  const [activeTileId, setActiveTileId] = useState(null);
  const [isSentencePlaying, setIsSentencePlaying] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [boardInteractionLocked, setBoardInteractionLocked] = useState(false);

  const speakingQueueRef = useRef([]);
  const activeTileIdRef = useRef(null);
  const isSpeakingRef = useRef(false);
  const lastSentenceTouchRef = useRef({ tileId: null, time: 0 });

  // Briefly block tile input after changing boards so the touch that opened a
  // folder cannot activate a tile on the destination board.
  //
  // Normal/direct board loads use a short default. Folder navigation overrides
  // this with the estimated speaking duration of the folder label.
  const boardInteractionLockedUntilRef = useRef(0);
  const boardInteractionLockMsRef = useRef(500);
  const DEFAULT_BOARD_INTERACTION_LOCK_MS = 500;

  useEffect(() => { speakingQueueRef.current = speakingQueue; }, [speakingQueue]);
  useEffect(() => { activeTileIdRef.current = activeTileId; }, [activeTileId]);
  useEffect(() => { isSpeakingRef.current = isSpeaking; }, [isSpeaking]);

  const isAndroidWebView =
    /wv/.test(navigator.userAgent.toLowerCase()) || window.AndroidTTS;

  // === Calculate tile dimensions ===
  const calculateGridSettings = (
    rows,
    cols,
    scrollable,
    squareButtons,
    availableWidth = window.innerWidth
  ) => {
    const windowHeight = window.innerHeight - HEADER_HEIGHT - (showSentenceBuilder ? SENTENCE_BUILDER_HEIGHT : 0);
    const windowWidth = Math.max(180, availableWidth);
    const gap = minTileGap;

    let tileWidth, tileHeight, allowScrollY;

    if (scrollable) {
      // Scrollable mode — fit width only
      tileWidth = (windowWidth - gap * (cols + 4)) / cols;
      tileHeight = squareButtons ? tileWidth : Math.max(tileWidth * 0.5, 80);
      allowScrollY = true;
    } else {
      // Non-scrollable mode — fit to both width and height
      const maxByWidth = (windowWidth - gap * (cols + 2)) / cols;
      const maxByHeight = (windowHeight - gap * (rows + 2)) / rows;
      if(squareButtons){
        tileWidth = tileHeight = Math.floor(Math.min(maxByWidth, maxByHeight));
      } else {
        tileWidth = maxByWidth;
        tileHeight = maxByHeight;
      }
      allowScrollY = false;
    }

    const columnGap = gap;
    const rowGap = gap;

    tileWidth = Math.round(tileWidth);
    tileHeight = Math.round(tileHeight);

    return {
      columns: cols,
      rows,
      TILE_WIDTH: tileWidth,
      TILE_HEIGHT: tileHeight,
      cTILE_GAP: columnGap,
      rTILE_GAP: rowGap,
      allowScrollY,
    };
  };

  const stickyCoreCacheKey = () => {
    try {
      const token = localStorage.getItem('token');
      const payload = token ? JSON.parse(atob(token.split('.')[1])) : null;
      return `stickyCoreSettings:${payload?.sub || 'current'}`;
    } catch {
      return 'stickyCoreSettings:current';
    }
  };

  const applyCoreSettings = (settings) => {
    if (!settings || typeof settings !== 'object') return;
    setCoreSettings({
      enabled: Boolean(settings.enabled),
      board_id: settings.board_id ?? null,
      side: settings.side === 'right' ? 'right' : 'left',
      selected_board: settings.selected_board ?? null,
      core_boards: Array.isArray(settings.core_boards) ? settings.core_boards : [],
    });

    try {
      localStorage.setItem(stickyCoreCacheKey(), JSON.stringify(settings));
    } catch {}
  };

  const loadCoreSettings = async () => {
    if (!isUnlimited) {
      setCoreSettings({ enabled: false, board_id: null, side: 'left', selected_board: null, core_boards: [] });
      return;
    }

    // Do not make a core-settings.php request while offline. Unlike board/tile
    // data this preference endpoint is not part of the offline API cache.
    if (!navigator.onLine) {
      try {
        const cached = JSON.parse(
          localStorage.getItem(stickyCoreCacheKey()) || 'null'
        );
        if (cached) applyCoreSettings(cached);
      } catch {}
      return;
    }

    try {
      const { data } = await api.get('/core-settings.php');
      applyCoreSettings(data);
    } catch (err) {
      try {
        const cached = JSON.parse(localStorage.getItem(stickyCoreCacheKey()) || 'null');
        if (cached) applyCoreSettings(cached);
      } catch {}
      console.debug('Could not load Sticky Core settings:', err);
    }
  };

  useEffect(() => {
    loadCoreSettings();

    const handleCoreChanged = (event) => {
      if (event?.detail) applyCoreSettings(event.detail);
      else loadCoreSettings();
    };

    const handleOnline = () => loadCoreSettings();

    window.addEventListener('sticky-core-changed', handleCoreChanged);
    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('sticky-core-changed', handleCoreChanged);
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchCoreBoard = async () => {
      if (!stickyCoreActive) {
        setCoreTiles([]);
        return;
      }

      try {
        const [boardRes, tileRes] = await Promise.all([
          api.get(`/getconfig.php?id=${coreSettings.board_id}`),
          api.get(`/tiles.php?board_id=${coreSettings.board_id}`),
        ]);

        if (cancelled) return;

        const cfg = JSON.parse(boardRes.data?.grid_config || '{}');
        setCoreGridConfig({
          rows: Math.min(6, Math.max(1, Number(cfg.rows) || 6)),
          cols: Math.min(4, Math.max(1, Number(cfg.columns || cfg.cols) || 4)),
          scrollable:
            String(boardRes.data?.scrollable) === '1' ||
            boardRes.data?.scrollable === 1 ||
            boardRes.data?.scrollable === true,
          square_buttons:
            String(boardRes.data?.square_buttons) === '1' ||
            boardRes.data?.square_buttons === 1 ||
            boardRes.data?.square_buttons === true,
        });

        const sorted = Array.isArray(tileRes.data)
          ? [...tileRes.data].sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
          : [];
        setCoreTiles(sorted);

        await preloadImages(
          sorted.map((t) => (t.image_url || '').trim()).filter(Boolean)
        );
      } catch (err) {
        console.error('Failed to load Core Board:', err);
        setCoreTiles([]);
        showToast('Core Board not available.', 'warning', false);
      }
    };

    fetchCoreBoard();
    return () => {
      cancelled = true;
    };
  }, [stickyCoreActive, coreSettings.board_id]);

  // === Recalculate on resize ===
  useEffect(() => {
    const updateGrid = () => {
      const totalWidth = window.innerWidth;
      const coreWidth = stickyCoreActive ? Math.floor(totalWidth * 0.38) : 0;
      const contentWidth = stickyCoreActive
        ? Math.floor(totalWidth * 0.62)
        : totalWidth;

      setGridSettings(
        calculateGridSettings(
          gridConfig.rows,
          gridConfig.cols,
          gridConfig.scrollable,
          gridConfig.square_buttons,
          contentWidth
        )
      );

      if (stickyCoreActive) {
        setCoreGridSettings(
          calculateGridSettings(
            coreGridConfig.rows,
            coreGridConfig.cols,
            coreGridConfig.scrollable,
            coreGridConfig.square_buttons,
            coreWidth
          )
        );
      }
    };

    updateGrid();
    window.addEventListener('resize', updateGrid);
    return () => window.removeEventListener('resize', updateGrid);
  }, [gridConfig, coreGridConfig, stickyCoreActive, showSentenceBuilder]);

  // === Preload images ===
  const preloadImages = async (urls) => {
    const unique = Array.from(new Set(urls.filter(Boolean)));
    if (unique.length === 0) return;
    let completed = 0;
    const step = IMG_PROGRESS_PORTION / unique.length;
    await Promise.all(
      unique.map(
        (url) =>
          new Promise((resolve) => {
            const img = new Image();
            const done = () => {
              completed += 1;
              setLoaderProgress((prev) =>
                Math.min(99, API_PROGRESS_PORTION + Math.floor(completed * step))
              );
              resolve();
            };
            img.onload = done;
            img.onerror = done;
            img.decoding = 'async';
            img.src = url;
            if (img.complete) done();
          })
      )
    );
  };

  // === Fetch board + config ===
  useEffect(() => {
    // Navigation stays instant, but the destination grid cannot receive pointer
    // input for the safety window chosen by the navigation that opened it.
    const interactionLockMs =
      boardInteractionLockMsRef.current || DEFAULT_BOARD_INTERACTION_LOCK_MS;

    boardInteractionLockedUntilRef.current = Date.now() + interactionLockMs;
    setBoardInteractionLocked(true);

    const unlockTimer = window.setTimeout(() => {
      setBoardInteractionLocked(false);
    }, interactionLockMs);

    // Future non-folder/direct board loads fall back to the short default.
    boardInteractionLockMsRef.current = DEFAULT_BOARD_INTERACTION_LOCK_MS;

    const fetchBoard = async () => {
      setShowLoader(true);
      setLoaderProgress(5);
      try {
        const [boardRes, res] = await Promise.all([
          api.get(`/getconfig.php?id=${id}`),
          api.get(`/tiles.php?board_id=${id}`),
        ]);

        let parsedGrid = {
          rows: DEFAULT_GRID.rows,
          cols: DEFAULT_GRID.cols,
          scrollable: false,
          square_buttons: true,
        };

        if (boardRes.data) {
          try {
            const cfg = JSON.parse(boardRes.data.grid_config || '{}');
            parsedGrid = {
              rows: cfg.rows > 0 ? cfg.rows : DEFAULT_GRID.rows,
              cols: cfg.columns > 0 ? cfg.columns : DEFAULT_GRID.cols,
              scrollable:
                String(boardRes.data.scrollable) === '1' ||
                boardRes.data.scrollable === 1 ||
                boardRes.data.scrollable === true,
              square_buttons:
                String(boardRes.data.square_buttons) === '1' ||
                boardRes.data.square_buttons === 1 ||
                boardRes.data.square_buttons === true,
            };
            
          } catch (e) {
            console.warn('Invalid grid_config JSON, using defaults.');
          }
        } else {
          console.warn('No board data from getconfig.php, using defaults.');
        }

        setGridConfig(parsedGrid);

        // --- Load tiles ---
        const sorted = res.data.sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
        setTiles(sorted);
        setLoaderProgress(API_PROGRESS_PORTION);

        // --- Preload images ---
        const imageUrls = sorted.map((t) => (t.image_url || '').trim()).filter(Boolean);
        await preloadImages(imageUrls);
      } catch (err) {
        console.error('Failed to load board:', err);
      } finally {
        setLoaderProgress(100);
        setTimeout(() => {
          setShowLoader(false);
          setLoaderProgress(0);
        }, 250);
        setLoading(false);
      }
    };

    fetchBoard();

    // fullscreen + wakelock
    if (document.documentElement.requestFullscreen && !document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(console.error);
    }

    let wakeLock;
    if ('wakeLock' in navigator && navigator.wakeLock.request) {
      navigator.wakeLock
        .request('screen')
        .then((lock) => {
          wakeLock = lock;
          lock.addEventListener('release', () => (wakeLock = null));
        })
        .catch(console.error);
    }

    return () => {
      window.clearTimeout(unlockTimer);
      if (wakeLock) {
        wakeLock.release().catch(console.error);
      }
    };
  }, [id]);

  // === Speech logic ===
  const estimateDuration = (text) => {
    const words = text.trim().split(/\s+/).length;
    return Math.max(1000, words * 600);
  };

  useEffect(() => {
    const speakNext = () => {
      const synth = window.speechSynthesis;
      const queue = speakingQueueRef.current;

      if (queue.length > 0 && !isSpeakingRef.current) {
        const next = queue[0];
        setIsSpeaking(true);
        setActiveTileId(next.id);

        const finish = () => {
          if (next.isFolder && typeof next.postAction === 'function') {
            setSpeakingQueue([]);
            next.postAction();
          } else {
            setSpeakingQueue((prev) => prev.slice(1));
          }
          setActiveTileId(null);
          setIsSpeaking(false);
        };

        if (synth && typeof synth.speak === 'function') {
          const utter = new SpeechSynthesisUtterance(next.label);
          utter.lang = 'en-GB';
          utter.onend = finish;
          synth.speak(utter);
          if (isAndroidWebView) {
            setTimeout(finish, estimateDuration(next.label));
          }
        } else if (window.AndroidTTS?.speak) {
          window.AndroidTTS.speak(next.label);
          setTimeout(finish, estimateDuration(next.label));
        } else {
          finish();
        }
      }
    };

    const intervalId = setInterval(speakNext, 100);
    return () => clearInterval(intervalId);
  }, []);

  // === Sentence Builder ===
  const playSentence = () => {
    if (!showSentenceBuilder || sentenceTiles.length === 0 || isSentencePlaying) return;

    const sentence = sentenceTiles
      .map((tile) => (tile?.label || '').trim())
      .filter(Boolean)
      .join(' ');

    if (!sentence) return;

    setIsSentencePlaying(true);

    let finished = false;
    const finishSentence = () => {
      if (finished) return;
      finished = true;
      setIsSentencePlaying(false);
    };

    const synth = window.speechSynthesis;

    if (synth && typeof synth.speak === 'function') {
      const utter = new SpeechSynthesisUtterance(sentence);
      utter.lang = 'en-GB';
      utter.onend = finishSentence;
      utter.onerror = finishSentence;
      synth.speak(utter);

      if (isAndroidWebView) {
        setTimeout(finishSentence, estimateDuration(sentence));
      }
    } else if (window.AndroidTTS?.speak) {
      window.AndroidTTS.speak(sentence);
      setTimeout(finishSentence, estimateDuration(sentence));
    } else {
      finishSentence();
    }
  };

  const clearSentence = (event) => {
    event.stopPropagation();
    setSentenceTiles([]);
  };

  const readBoardHistory = () => {
    try {
      const parsed = JSON.parse(sessionStorage.getItem(BOARD_HISTORY_KEY) || '[]');
      return Array.isArray(parsed) ? parsed.map(String).filter(Boolean) : [];
    } catch {
      return [];
    }
  };

  const writeBoardHistory = (history) => {
    try {
      sessionStorage.setItem(BOARD_HISTORY_KEY, JSON.stringify(history));
    } catch {}
  };

  const navigateWithinBoardView = (destinationBoardId, { pushCurrent = true } = {}) => {
    const destination = String(destinationBoardId || '');
    const current = String(id || '');
    if (!destination || destination === current) return false;

    if (pushCurrent) {
      const history = readBoardHistory();
      if (history[history.length - 1] !== current) history.push(current);
      writeBoardHistory(history.slice(-50));
    }

    try {
      sessionStorage.setItem(BOARD_EXPECTED_DESTINATION_KEY, destination);
    } catch {}

    navigate(`/board/${destination}`);
    return true;
  };

  const goBackWithinBoardView = () => {
    const history = readBoardHistory();
    const current = String(id || '');

    while (history.length > 0) {
      const previous = String(history.pop() || '');
      if (!previous || previous === current) continue;

      writeBoardHistory(history);
      try {
        sessionStorage.setItem(BOARD_EXPECTED_DESTINATION_KEY, previous);
      } catch {}
      navigate(`/board/${previous}`);
      return true;
    }

    writeBoardHistory([]);
    showToast('No previous board to go back to.', 'info', false);
    return false;
  };

  // Clear stale board-only history when BoardView is entered from somewhere
  // other than one of our own folder/back navigations.
  useEffect(() => {
    try {
      const expected = sessionStorage.getItem(BOARD_EXPECTED_DESTINATION_KEY);
      if (expected && String(expected) === String(id)) {
        sessionStorage.removeItem(BOARD_EXPECTED_DESTINATION_KEY);
      } else {
        writeBoardHistory([]);
        sessionStorage.removeItem(BOARD_EXPECTED_DESTINATION_KEY);
      }
    } catch {}
  }, [id]);

  // === Tile click handler ===
  const handleTileClick = (tile, inputType = 'click') => {
    if (Date.now() < boardInteractionLockedUntilRef.current) {
      return;
    }

    if (isAccessExpired()) {
      showToast('Access expired. Please upgrade your plan.', 'error', false);
      localStorage.removeItem('token');
      navigate('/login');
      return;
    }

    if (
      (!tile.label || tile.label.trim() === '') &&
      (!tile.image_url || tile.image_url.trim() === '')
    ) {
      return;
    }

    const isBackButton = Boolean(Number(tile.is_back_button || 0));
    const isSilentFolder =
      Boolean(tile.is_folder && tile.linked_board_id) &&
      Boolean(Number(tile.folder_silent || 0));

    // Back is a navigation control: never speak it and never add it to the
    // Sentence Builder. It can be placed on either the content or Sticky Core grid.
    if (isBackButton) {
      goBackWithinBoardView();
      return;
    }

    if (showSentenceBuilder && !isSilentFolder) {
      const now = Date.now();
      const isSyntheticClickAfterTouch =
        inputType === 'click' &&
        lastSentenceTouchRef.current.tileId === tile.id &&
        now - lastSentenceTouchRef.current.time < 800;

      if (inputType === 'touch') {
        lastSentenceTouchRef.current = { tileId: tile.id, time: now };
      }

      if (!isSyntheticClickAfterTouch) {
        setSentenceTiles((prev) => {
          const lastTile = prev[prev.length - 1];

          // Prevent the same tile being added repeatedly in a row.
          // It can still be used again later in the sentence after another tile.
          if (lastTile && String(lastTile.id) === String(tile.id)) {
            return prev;
          }

          return [
          ...prev,
          {
            id: tile.id,
            label: tile.label,
            image_url: tile.image_url || '',
          },
          ];
        });
      }
    }

    const speakItem = {
      id: tile.id,
      label: tile.label,
      isFolder: tile.is_folder && tile.linked_board_id,
      folderSilent: Boolean(Number(tile.folder_silent || 0)),
    };

    if (speakItem.isFolder) {
      const destinationBoardId = String(tile.linked_board_id);
      const currentBoardId = String(id);

      // If this folder points to the board that is already displayed in the
      // content pane, there is nothing to navigate to.
      //
      // Previously we still enabled boardInteractionLocked here, but React
      // Router correctly ignored navigation to the same /board/:id route.
      // Because the [id] effect never ran, its unlock timer never ran either,
      // leaving both Core and content grids permanently unclickable.
      if (destinationBoardId !== currentBoardId) {
      // Keep the visual navigation instant, but prevent interaction with the
        // destination board for approximately as long as the folder label
        // takes to speak.
      const folderInteractionLockMs = estimateDuration(tile.label);

      boardInteractionLockMsRef.current = folderInteractionLockMs;
      boardInteractionLockedUntilRef.current =
        Date.now() + folderInteractionLockMs;
      setBoardInteractionLocked(true);

      navigateWithinBoardView(tile.linked_board_id);
    }
    }

    // Silent folders still navigate normally, but do not enter the speech queue.
    if (!(speakItem.isFolder && speakItem.folderSilent)) {
    setSpeakingQueue((prev) => {
      if (prev.some((item) => item.id === tile.id)) return prev;
      if (prev.length >= QUEUE_LIMIT) {
        showToast(`Speaking queue is full (max ${QUEUE_LIMIT}).`, 'warning', false);
        return prev;
      }
      return [...prev, speakItem];
    });
    }
  };

  const renderBoardGrid = (boardTiles, settings, isCore = false) => (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${settings.columns}, ${settings.TILE_WIDTH}px)`,
        columnGap: '10px',
        rowGap: '10px',
        height: settings.allowScrollY
          ? 'auto'
          : `calc(100vh - ${HEADER_HEIGHT + (showSentenceBuilder ? SENTENCE_BUILDER_HEIGHT : 0)}px)`,
        maxHeight: `calc(100vh - ${HEADER_HEIGHT + (showSentenceBuilder ? SENTENCE_BUILDER_HEIGHT : 0)}px)`,
        overflowY: settings.allowScrollY ? 'auto' : 'hidden',
        justifyContent: 'center',
        alignContent: 'start',
        overscrollBehavior: 'none',
        padding: '10px',
        boxSizing: 'border-box',
        pointerEvents: boardInteractionLocked ? 'none' : 'auto',
      }}
    >
      {boardTiles.map((tile) => (
        <button
          key={`${isCore ? 'core' : 'board'}-${tile.id}`}
          onTouchStart={() => handleTileClick(tile, 'touch')}
          onClick={() => handleTileClick(tile, 'click')}
          style={{
            backgroundColor: tile.color || '#eeeeee',
            borderRadius: tile.is_folder ? '8px 30px 8px 8px' : '8px',
            width: `${settings.TILE_WIDTH}px`,
            height: `${settings.TILE_HEIGHT}px`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            border:
              activeTileId === tile.id
                ? '3px solid #ff9800'
                : tile.is_folder
                  ? '2px solid #ccc'
                  : '1px solid #ccc',
            boxShadow:
              activeTileId === tile.id
                ? '0 0 10px rgba(255, 152, 0, 0.6)'
                : '0 1px 3px rgba(0,0,0,0.1)',
            overflow: 'hidden',
            userSelect: 'none',
            touchAction: 'manipulation',
          }}
        >
          {tile.image_url && (
            <img
              src={tile.image_url}
              alt={tile.label}
              style={{
                width: '65%',
                height: '65%',
                objectFit: 'contain',
                marginBottom: '4px',
                pointerEvents: 'none',
              }}
              draggable={false}
            />
          )}
          <span
            style={{
              fontSize: '0.85rem',
              fontWeight: '500',
              textAlign: 'center',
              userSelect: 'none',
            }}
          >
            {tile.label}
          </span>
        </button>
      ))}
    </div>
  );

  // === Loader ===
  const TopLoader = () =>
    showLoader ? (
      <div className="top-loader">
        <div
          className="top-loader__bar"
          style={{ width: `${Math.max(0, Math.min(100, loaderProgress))}%` }}
        />
      </div>
    ) : null;

  // === Render ===
  return (
    <div style={{ overflow: 'hidden', backgroundColor: '#fff' }}>
      <TopLoader />

      {showSentenceBuilder && (
        <div
          onClick={playSentence}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') playSentence();
          }}
          aria-label="Play sentence"
          style={{
            height: `${SENTENCE_BUILDER_HEIGHT}px`,
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '6px 8px',
            margin: '0 10px',
            border: isSentencePlaying
              ? '3px solid #ff9800'
              : '2px solid #1976d2',
            borderRadius: '8px',
            backgroundColor: '#f7fbff',
            boxShadow: isSentencePlaying
              ? '0 0 10px rgba(255, 152, 0, 0.6)'
              : 'none',
            cursor: sentenceTiles.length > 0 ? 'pointer' : 'default',
            userSelect: 'none',
          }}
        >
          <div
            style={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              overflowX: 'auto',
              overflowY: 'hidden',
              height: '100%',
            }}
          >
            {sentenceTiles.length === 0 ? (
              <span style={{ color: '#666', paddingLeft: '6px' }}>
                Select tiles to build a sentence
              </span>
            ) : (
              sentenceTiles.map((tile, index) => (
                <div
                  key={`${tile.id}-${index}`}
                  style={{
                    flex: '0 0 auto',
                    height: '58px',
                    minWidth: '64px',
                    maxWidth: '100px',
                    padding: '3px 6px',
                    boxSizing: 'border-box',
                    border: '1px solid #ccc',
                    borderRadius: '6px',
                    backgroundColor: '#fff',
                    boxShadow: 'none',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {tile.image_url && (
                    <img
                      src={tile.image_url}
                      alt=""
                      draggable={false}
                      style={{
                        width: '30px',
                        height: '30px',
                        objectFit: 'contain',
                        pointerEvents: 'none',
                      }}
                    />
                  )}
                  <span
                    style={{
                      maxWidth: '88px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontSize: '12px',
                      fontWeight: 600,
                    }}
                  >
                    {tile.label}
                  </span>
                </div>
              ))
            )}
          </div>

          <button
            type="button"
            onClick={clearSentence}
            disabled={sentenceTiles.length === 0}
            style={{
              flex: '0 0 auto',
              height: '44px',
              padding: '0 14px',
              border: 'none',
              borderRadius: '6px',
              backgroundColor: sentenceTiles.length > 0 ? '#d32f2f' : '#aaa',
              color: '#fff',
              fontWeight: 'bold',
              cursor: sentenceTiles.length > 0 ? 'pointer' : 'default',
            }}
          >
            🗑️<br></br>
            Clear
          </button>
        </div>
      )}

      {stickyCoreActive ? (
        <div
          style={{
            display: 'flex',
            flexDirection: coreSettings.side === 'right' ? 'row-reverse' : 'row',
            width: '100%',
            height: `calc(100vh - ${HEADER_HEIGHT + (showSentenceBuilder ? SENTENCE_BUILDER_HEIGHT : 0)}px)`,
            overflow: 'hidden',
            background: '#fff',
          }}
        >
          <section
            aria-label={`Core Board ${coreSettings?.selected_board?.name || ''}`}
            style={{
              width: '38%',
              minWidth: 0,
              height: '100%',
              overflow: 'hidden',
              background: '#f8fbff',
              borderRight: coreSettings.side === 'left' ? '2px solid #d9e3ef' : 'none',
              borderLeft: coreSettings.side === 'right' ? '2px solid #d9e3ef' : 'none',
            }}
          >
            {renderBoardGrid(coreTiles, coreGridSettings, true)}
          </section>

          <section
            aria-label="Current Board"
            style={{
              width: '62%',
              minWidth: 0,
              height: '100%',
              overflow: 'hidden',
            }}
          >
            {renderBoardGrid(tiles, gridSettings, false)}
          </section>
        </div>
      ) : (
        renderBoardGrid(tiles, gridSettings, false)
      )}
    </div>
  );
};

export default BoardView;
